import React, {useCallback, useEffect, useState} from 'react';
import {InfoCard, Progress} from '@backstage/core-components';
import {discoveryApiRef, fetchApiRef, useApi} from '@backstage/core-plugin-api';
import {useEntity} from '@backstage/plugin-catalog-react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import SettingsIcon from '@material-ui/icons/Settings';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

type ConfluencePage = {
  id: string;
  title: string;
  url: string;
  lastUpdated: string;
  lastUpdatedBy: string;
  version: number;
  labels: string[];
};

type SearchResult = {
  id: string;
  title: string;
  url: string;
  excerpt: string;
};

type ConfluenceConfig = {
  id: number;
  entity_ref: string;
  confluence_email: string;
  confluence_token: string;
  space_key: string;
  confluence_url: string;
} | null;

const useStyles = makeStyles(theme => ({
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
    marginBottom: 16,
    flex: 1,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    marginLeft: 8,
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f7fa',
    },
  },
  chip: {
    height: 20,
    fontSize: 11,
    marginRight: 4,
  },
  excerpt: {
    color: '#666',
    fontSize: 12,
    marginTop: 4,
    lineHeight: 1.4,
  },
}));

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
}

export function ConfluenceEntityTab() {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const {entity} = useEntity();

  const entityName = entity.metadata.name;
  const entityRef = `${entity.kind}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`.toLowerCase();

  const [config, setConfig] = useState<ConfluenceConfig>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [pages, setPages] = useState<ConfluencePage[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Settings form state
  const [formEmail, setFormEmail] = useState('');
  const [formToken, setFormToken] = useState('');
  const [formSpaceKey, setFormSpaceKey] = useState('');
  const [formUrl, setFormUrl] = useState('https://sourcefuse.atlassian.net');
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('confluence-settings');
      const resp = await fetchApi.fetch(`${baseUrl}/config?entityRef=${encodeURIComponent(entityRef)}`);
      const data = await resp.json();
      setConfig(data);
      if (data) {
        setFormEmail(data.confluence_email || '');
        setFormSpaceKey(data.space_key || '');
        setFormUrl(data.confluence_url || 'https://sourcefuse.atlassian.net');
        setFormToken('');
      }
    } catch {
      // ignore
    } finally {
      setConfigLoaded(true);
    }
  }, [discoveryApi, fetchApi, entityRef]);

  const loadRecentPages = useCallback(async () => {
    if (!config?.space_key) return;
    setLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('confluence-settings');
      const params = new URLSearchParams({entityRef, entityName, spaceKey: config.space_key});
      const resp = await fetchApi.fetch(`${baseUrl}/recent?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        setPages(data);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi, entityRef, entityName, config]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (config?.space_key && config?.confluence_email) loadRecentPages();
  }, [config, loadRecentPages]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('confluence-settings');
      const resp = await fetchApi.fetch(`${baseUrl}/config`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          entityRef,
          confluenceEmail: formEmail,
          confluenceToken: formToken,
          spaceKey: formSpaceKey,
          confluenceUrl: formUrl,
        }),
      });
      const data = await resp.json();
      setConfig(data);
      setFormToken('');
      setSettingsOpen(false);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('confluence-settings');
      const resp = await fetchApi.fetch(`${baseUrl}/search`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({query: q, entityRef, entityName, spaceKey: config?.space_key}),
      });
      if (resp.ok) {
        const data = await resp.json();
        setSearchResults(data);
      }
    } catch {
      // ignore
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  if (!configLoaded) return <Progress />;

  const isConfigured = config && config.confluence_email && config.space_key;

  if (!isConfigured) {
    return (
      <InfoCard title="Confluence">
        <Box textAlign="center" py={4}>
          <Typography variant="body1" style={{marginBottom: 16, color: '#666'}}>
            Confluence is not configured for this entity. Set up credentials and space key to view pages.
          </Typography>
          <Button
            variant="contained"
            style={{backgroundColor: '#0469E3', color: '#fff'}}
            onClick={() => setSettingsOpen(true)}
            startIcon={<SettingsIcon />}
          >
            Configure Confluence
          </Button>
        </Box>
        {renderSettingsDialog()}
      </InfoCard>
    );
  }

  function renderSettingsDialog() {
    return (
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confluence Settings — {entityName}</DialogTitle>
        <DialogContent>
          <TextField
            label="Confluence URL"
            fullWidth
            margin="dense"
            value={formUrl}
            onChange={e => setFormUrl(e.target.value)}
            placeholder="https://your-org.atlassian.net"
          />
          <TextField
            label="Email ID"
            fullWidth
            margin="dense"
            value={formEmail}
            onChange={e => setFormEmail(e.target.value)}
            placeholder="your-email@company.com"
          />
          <TextField
            label="API Token"
            fullWidth
            margin="dense"
            type="password"
            value={formToken}
            onChange={e => setFormToken(e.target.value)}
            placeholder={config ? '(unchanged — enter new to update)' : 'Confluence API token'}
          />
          <TextField
            label="Space Key"
            fullWidth
            margin="dense"
            value={formSpaceKey}
            onChange={e => setFormSpaceKey(e.target.value)}
            placeholder="e.g. DEV, TEAM"
            helperText="Confluence space key for this entity"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button
            onClick={saveConfig}
            variant="contained"
            disabled={saving || !formEmail || !formSpaceKey.trim() || (!config && !formToken)}
            style={{backgroundColor: '#0469E3', color: '#fff'}}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const showingSearch = searchResults !== null;
  const spaceKey = config.space_key;

  return (
    <>
      <InfoCard
        title={
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Box display="flex" alignItems="center">
              <Typography variant="h6" style={{fontWeight: 600, marginRight: 8}}>
                Confluence
              </Typography>
              {spaceKey && (
                <Chip
                  label={spaceKey}
                  size="small"
                  style={{
                    backgroundColor: '#e3f2fd',
                    color: '#0469E3',
                    fontWeight: 600,
                    fontSize: 11,
                    height: 22,
                  }}
                />
              )}
            </Box>
            <Box>
              <Tooltip title="Settings">
                <IconButton size="small" onClick={() => setSettingsOpen(true)}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={loadRecentPages} disabled={loading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        }
      >
        {/* Search bar */}
        <Box display="flex" alignItems="center" mb={2}>
          <Paper className={classes.searchBar} elevation={0}>
            <SearchIcon style={{color: '#999', fontSize: 20}} />
            <InputBase
              className={classes.searchInput}
              placeholder="Search Confluence pages..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            {searchQuery && (
              <IconButton size="small" onClick={clearSearch}>
                <ClearIcon style={{fontSize: 18}} />
              </IconButton>
            )}
          </Paper>
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            disabled={searching || !searchQuery.trim()}
            style={{
              backgroundColor: '#0469E3',
              color: '#fff',
              marginLeft: 8,
              minWidth: 80,
              height: 36,
            }}
          >
            {searching ? <CircularProgress size={18} style={{color: '#fff'}} /> : 'Search'}
          </Button>
        </Box>

        {/* Search results */}
        {showingSearch && (
          <Box mb={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2" style={{color: '#666'}}>
                Search results for "{searchQuery}" — {searchResults!.length} found
              </Typography>
              <Button size="small" onClick={clearSearch} style={{textTransform: 'none'}}>
                Back to recent pages
              </Button>
            </Box>
            {searchResults!.length === 0 ? (
              <Box textAlign="center" py={3}>
                <Typography variant="body2" style={{color: '#999'}}>
                  No pages found matching your query.
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{fontWeight: 600}}>Title</TableCell>
                    <TableCell style={{fontWeight: 600}}>Excerpt</TableCell>
                    <TableCell style={{fontWeight: 600, width: 50}} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults!.map(r => (
                    <TableRow key={r.id} className={classes.tableRow}>
                      <TableCell>
                        <Typography variant="body2" style={{fontWeight: 500}}>
                          {r.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" className={classes.excerpt}>
                          {r.excerpt || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Open in Confluence">
                          <IconButton
                            size="small"
                            component={Link}
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNewIcon style={{fontSize: 18, color: '#0469E3'}} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {/* Recent pages (default view) */}
        {!showingSearch && (
          <>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            ) : pages.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body2" style={{color: '#999'}}>
                  No pages found in this space.
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="subtitle2" style={{color: '#666', marginBottom: 8}}>
                  Recently Updated Pages
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{fontWeight: 600}}>Title</TableCell>
                      <TableCell style={{fontWeight: 600}}>Updated</TableCell>
                      <TableCell style={{fontWeight: 600}}>Updated By</TableCell>
                      <TableCell style={{fontWeight: 600, width: 60}}>Version</TableCell>
                      <TableCell style={{fontWeight: 600}}>Labels</TableCell>
                      <TableCell style={{fontWeight: 600, width: 50}} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pages.map(p => (
                      <TableRow key={p.id} className={classes.tableRow}>
                        <TableCell>
                          <Typography variant="body2" style={{fontWeight: 500}}>
                            {p.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{fontSize: 13, color: '#555'}}>
                            {formatDate(p.lastUpdated)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{fontSize: 13, color: '#555'}}>
                            {p.lastUpdatedBy || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`v${p.version}`}
                            size="small"
                            className={classes.chip}
                            style={{backgroundColor: '#f0f0f0'}}
                          />
                        </TableCell>
                        <TableCell>
                          {p.labels.length > 0
                            ? p.labels.map(l => (
                                <Chip
                                  key={l}
                                  label={l}
                                  size="small"
                                  className={classes.chip}
                                  style={{backgroundColor: '#e8f5e9', color: '#2e7d32'}}
                                />
                              ))
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Open in Confluence">
                            <IconButton
                              size="small"
                              component={Link}
                              href={p.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNewIcon style={{fontSize: 18, color: '#0469E3'}} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        )}
      </InfoCard>

      {renderSettingsDialog()}
    </>
  );
}
